import { Fragment, useCallback, useMemo, useState } from 'react';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import CheckIcon from '@mui/icons-material/Check';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { FiltersInstitution } from 'system/components/institution/list';
import { Institution } from 'shell/models/institution';
import { generatePath, useNavigate } from 'react-router-dom';
import { HttpResponseList } from 'shell/models/http-response';
import { useFormik } from 'formik';
import { initialValues } from '..';
import { useEffect } from 'react';
import { useFilterTable } from 'shell/hooks/use-filter-table';
// import { useUserProfile } from 'shell/hooks/use-user-profile';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { FiltersFormInstitution, InstitutionClient } from './types';
import { transformDataset } from './utils';
import { Operator } from 'shell/models/operator';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonInstitutionCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import ListHeader from 'shell/components/shared/list-header';
import { authsObj, headCells, validationSchemaInstitutionFilter } from './constants';
import { Typography } from '@mui/material';

const InstitutionPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<InstitutionClient>>({
    element: null,
    open: false,
  });

  const [deleteAlertModalOperators, setDeleteAlertModalOperators] = useState<
    AlertModal<InstitutionClient>
  >({
    element: null,
    open: false,
  });

  const [deleteAlertModalInstitution, setDeleteAlertModalInstitution] = useState<
    AlertModal<InstitutionClient>
  >({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<InstitutionClient>>({
    element: null,
    open: false,
  });

  const { delete: deleteInstitution } = useHttpFetch(`${__MANAGEMENT_API_URL__}/enti`);

  const { get: getOperators } = useHttpFetch<HttpResponseList<Operator>>(
    `${__MANAGEMENT_API_URL__}/operatori`
  );

  const filterTable = useFilterTable<FiltersFormInstitution>();

  // const { userProfile } = useUserProfile();

  const {
    data,
    get: getInstitutions,
    error,
  } = useHttpFetch<HttpResponseList<Institution>>(`${__MANAGEMENT_API_URL__}/enti/`, {
    data: {
      payload: {
        pageInfo: {
          totalCount: 0,
        },
        list: [],
      },
    },
  });

  const onSubmit = useCallback(
    (formValues: FiltersFormInstitution) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterServiceForm.setFieldValue('page', 0);
      getInstitutions(queryString);
    },
    [getInstitutions]
  );

  const filterServiceForm = useFormik({
    initialValues,
    validationSchema: validationSchemaInstitutionFilter,
    onSubmit,
  });

  useEffect(() => {
    filterServiceForm.setValues(filterTable.values || initialValues);
    const queryString = transformDataset(filterTable.values || initialValues);
    getInstitutions(queryString);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterServiceForm.values);
  }, [filterServiceForm.values]);

  const enti: Array<InstitutionClient> = useMemo(() => {
    if (!!error || !data?.payload) {
      return [];
    }
    return data.payload.list.map((ente) => ({
      istat: ente.codiceIstat,
      description: ente.nome,
      officeNumber: ente.numeroUfficiStatoCivile,
      province: ente.provincia,
      type: ente.tipo !== 'N' ? <CheckIcon /> : undefined,
      code: ente.key.codice,
    }));
  }, [data]);

  const count: number = useMemo(() => {
    if (!!error || !data?.payload || !data?.payload.pageInfo) {
      return 0;
    }
    return data.payload.pageInfo?.totalCount;
  }, [error, data?.payload]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const body = useMemo(() => {
    if (!enti) {
      return [];
    }
    return enti.map((ente) =>
      // TODO verificare che si debba mostrare o meno il cestino
      // userProfile.datiLogin?.profile?.servizio?.key?.codiceEnte || '' === ente.code
      //   ? {
      //       ...ente,
      //       hidden: {
      //         delete: true,
      //       },
      //     }
      //   : ente
      ({
        ...ente,
        hidden: {
          delete: true,
        },
      })
    );
  }, [enti]);

  const onVisibilityPress = useCallback(
    (cell): Promise<InstitutionClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/enti/dettaglio/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<InstitutionClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/enti/modifica/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<InstitutionClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<InstitutionClient>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      filterServiceForm.setFieldValue('page', newPage);
    },
    [filterServiceForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...filterServiceForm.values, rowsPerPage: rowsPerPage || 0, page: 0 };
      filterServiceForm.setValues(newDataset);
    },
    [filterServiceForm.setFieldValue, filterServiceForm.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterServiceForm.values, orders };
      const queryString = transformDataset(newDataset);
      return getInstitutions(queryString).then(() => {
        filterServiceForm.setFieldValue('orders', orders);
      });
    },
    [filterServiceForm.setFieldValue, getInstitutions, filterServiceForm.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={CommonInstitutionCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo ente"
        addBtnLabel="Aggiungi nuovo ente"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/enti/nuovo');
        }}
        title="Gestione Enti"
      />
      <div className="pmi-my-3">
        <FiltersInstitution filterServiceForm={filterServiceForm} />
      </div>
      {data?.payload ? (
        body?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterServiceForm.values.orders}
            activeActions={auths}
            page={filterServiceForm.values.page}
            rowsPerPage={filterServiceForm.values.rowsPerPage}
            body={body}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Enti"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Ente"
        message={`La cancellazione dell'ente elimina l'ente selezionato. Confermare la cancellazione?`}
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={async () => {
          const operators: HttpResponseList<Operator> = await getOperators(
            `/${deleteAlertModal.element?.code}`
          );
          setDeleteAlertModal({ open: false, element: null });
          if (operators.payload.list.length > 0) {
            setDeleteAlertModalOperators({
              open: true,
              element: {
                ...(deleteAlertModal.element || {
                  istat: '',
                  description: '',
                  officeNumber: 0,
                  province: '',
                  code: '',
                  type: undefined,
                }),
                operatorsNumber: operators.payload.list.length || 0,
              },
            });
            return;
          }
          setDeleteAlertModalInstitution({ open: true, element: deleteAlertModal.element });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteAlertModal
        title="Cancellazione Ente"
        message={`Ci sono ${deleteAlertModalOperators.element?.operatorsNumber} operatori collegati all'ente. 
        Procedere comunque con la cancellazione?`}
        open={deleteAlertModalOperators.open}
        onClose={() => setDeleteAlertModalOperators({ open: false, element: null })}
        onYes={() => {
          setDeleteAlertModalInstitution({
            open: true,
            element: deleteAlertModalOperators.element,
          });
          setDeleteAlertModalOperators({ open: false, element: null });
        }}
        onNo={() => setDeleteAlertModalOperators({ open: false, element: null })}
      />
      <DeleteAlertModal
        title="Cancellazione Ente"
        message={`Confermi la cancellazione dell'ente ${deleteAlertModalInstitution.element?.description}`}
        open={deleteAlertModalInstitution.open}
        onClose={() => setDeleteAlertModalInstitution({ open: false, element: null })}
        onYes={() => {
          deleteInstitution(`/${deleteAlertModalInstitution.element?.code}`).then(() => {
            setDeleteAlertModalInstitution({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModalInstitution.element });
          });
        }}
        onNo={() => setDeleteAlertModalInstitution({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Ente"
        message={`La cancellazione dell'ente ${deleteSuccessModal.element?.description} è avvenuta con successo.`}
        onClose={() => {
          const queryString = transformDataset({ ...filterServiceForm.values, page: 0 });
          getInstitutions(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
        onOk={() => {
          const queryString = transformDataset({ ...filterServiceForm.values, page: 0 });
          getInstitutions(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
      />
    </Fragment>
  );
};

InstitutionPage.displayName = 'Institution page component';

export default InstitutionPage;
